import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Car, ChevronDown, Gauge, Mail, MapPin, Phone, Wrench } from 'lucide-react';
import { BRANDS } from '../data/brands';
import { BrandLogo } from '../components/BrandLogo';
import { ServiceBanner } from '../components/ServiceBanner';
const HERO_IMAGE = 'https://autowallis.si/wp-content/uploads/2026/04/the-1-des-1500x756.jpg';
const CONTACTS: Record<string, {
  place: string;
  mail: string;
  phone: string;
}[]> = {
  bmw: [{
    place: 'BMW Ljubljana',
    mail: 'info@wallismotor.si',
    phone: '01 200 60 00'
  }, {
    place: 'BMW Trzin',
    mail: 'info@avto-aktiv.si',
    phone: '01 582 90 00'
  }, {
    place: 'BMW Kranj',
    mail: 'bmw@avto-aktiv.si',
    phone: '04 280 90 00'
  }, {
    place: 'BMW Koper',
    mail: 'koper@avto-aktiv.si',
    phone: '05 663 60 00'
  }, {
    place: 'BMW Nova Gorica',
    mail: 'info.ng@avto-aktiv.si',
    phone: '05 338 40 00'
  }]
};
export function BrandPage() {
  const {
    brandId
  } = useParams<{
    brandId: string;
  }>();
  const brand = BRANDS.find(b => b.id === brandId);
  if (!brand) {
    return <div className="mx-auto flex w-full max-w-[1360px] flex-col items-start gap-4 px-5 py-24 lg:px-10" data-id="element-196" data-mp="2sqzg929hoh0y">
        <h1 className="font-heading text-2xl font-bold text-aw-navy" data-id="element-197" data-mp="2ezum2fow4gy0">Znamke ni bilo mogoče najti</h1>
        <p className="text-sm text-slate-500" data-id="element-198" data-mp="2jyfngifvp6o1">Preverite naslov ali se vrnite na začetno stran.</p>
        <Link to="/" className="bg-aw-navy px-6 py-3 text-sm font-semibold text-white hover:bg-aw-navy-dark" data-id="element-199" data-mp="37ojgxwzcrq1b">
          Nazaj na domačo stran
        </Link>
      </div>;
  }
  const contacts = CONTACTS[brand.id] ?? [{
    place: `${brand.name} ${brand.locations[0]}`,
    mail: 'info@autowallis.si',
    phone: '01 200 60 00'
  }];
  const actions = [{
    label: `Zaloga vozil ${brand.name}`,
    Icon: Car,
    text: 'Nova in malo rabljena vozila.'
  }, {
    label: 'Prijava na testno vožnjo',
    Icon: Gauge,
    text: 'Izberite model in termin.'
  }, ...(brand.hasService ? [{
    label: 'Servis in cenitev',
    Icon: Wrench,
    text: 'Pooblaščeni servis in nadomestni deli.'
  }] : [])];
  return <>
      {/* Hero znamke — glava znamke leži nad njim */}
      <section className="relative w-full overflow-hidden bg-aw-navy-dark" data-id="element-200" data-mp="yf25bdh7f3ux">
        <img src={HERO_IMAGE} alt={`Vozila ${brand.name}`} className="absolute inset-0 h-full w-full object-cover" data-id="element-201" data-mp="tgik5fafpw3m" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/45 to-transparent" data-id="element-202" data-mp="oi7cnb7m3xok" />

        <div className="relative mx-auto flex min-h-[460px] w-full max-w-[1360px] flex-col justify-center px-5 py-20 lg:min-h-[560px] lg:px-10" data-id="element-203" data-mp="jgrovbe1vd2h">
          <div className="max-w-2xl border-l-2 border-white/70 pl-6" data-id="element-204" data-mp="ahwhhvz9rwxl">
            <BrandLogo brand={brand} color="white" size={26} data-id="element-205" data-mp="ajpqvj19ov9a" />
            <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-white sm:text-4xl lg:text-5xl" data-id="element-206" data-mp="29tpj65x05qgn">
              Izjemna ponudba malo rabljenih službenih vozil {brand.name}
            </h1>
            <a href="#" className="mt-8 inline-block bg-aw-accent px-7 py-3 text-sm font-semibold text-white transition-colors hover:bg-white hover:text-aw-navy" data-id="element-207" data-mp="2cedplryvgrdk">
              Ogled zaloge
            </a>
          </div>

          <p className="mt-10 border-l border-white/40 pl-6 text-sm uppercase tracking-[0.06em] text-white/60" data-id="element-208" data-mp="2r3tet3ir8or5">
            <a href="#" className="hover:text-white" data-id="element-209" data-mp="1th45s9pmxbvs">
              Več o {brand.name} serije 3
            </a>
          </p>
        </div>

        <div className="relative flex justify-center pb-6 text-white/70" data-id="element-210" data-mp="1yfnu2grpazrb">
          <ChevronDown className="h-6 w-6 animate-bounce" aria-hidden="true" data-id="element-211" data-mp="23dfih0t503qr" />
        </div>
      </section>

      {/* Hitre akcije znamke */}
      <section aria-labelledby="brand-actions-title" className="w-full bg-slate-50" data-id="element-212" data-mp="27dr34dli2428">
        <div className="mx-auto w-full max-w-[1360px] px-5 py-14 lg:px-10" data-id="element-213" data-mp="3ete19we4a456">
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-aw-accent" data-id="element-214" data-mp="3itxuiovkknm1">
            {brand.tagline}
          </p>
          <h2 id="brand-actions-title" className="mt-2 font-heading text-2xl font-bold text-aw-navy sm:text-3xl" data-id="element-215" data-mp="3hg6c01hss62w">
            Kaj lahko storimo za vas?
          </h2>

          <ul className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-216" data-mp="3r9wvst2zbsc9">
            {actions.map(({
            label,
            Icon,
            text
          }) => <li key={label} data-id="element-217" data-mp="471tbytln209">
                <a href="#" className="group flex h-full flex-col border border-slate-200 bg-white p-6 transition-shadow hover:shadow-lg" data-id="element-218" data-mp="468url4snit7">
                  <Icon className="h-6 w-6 text-aw-navy" aria-hidden="true" data-id="element-219" data-mp="dw1vts7b8fj7" />
                  <span className="mt-8 font-heading text-lg font-semibold text-aw-navy" data-id="element-220" data-mp="8y0d58dsqi89">
                    {label}
                  </span>
                  <span className="mt-1 text-sm text-slate-500" data-id="element-221" data-mp="myb3mab71usw">{text}</span>
                  <span className="mt-6 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.1em] text-aw-accent" data-id="element-222" data-mp="ixr81b69qz8v">
                    Odpri
                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden="true" data-id="element-223" data-mp="16jl4wg0cizpu" />
                  </span>
                </a>
              </li>)}
          </ul>

          <ServiceBanner className="mt-10 min-h-[280px]" data-id="element-224" data-mp="xkzp2sm9dysr" />
        </div>
      </section>

      {/* Prodajno-servisni centri */}
      <section aria-labelledby="brand-contacts-title" className="w-full bg-white" data-id="element-225" data-mp="nlikyfmnnsyw">
        <div className="mx-auto w-full max-w-[1360px] px-5 py-14 lg:px-10" data-id="element-226" data-mp="9tkl6veu39fe">
          <h2 id="brand-contacts-title" className="font-heading text-2xl font-bold text-aw-navy sm:text-3xl" data-id="element-227" data-mp="9rhk9gc57vvw">
            Prodajno-servisni centri {brand.name}
          </h2>
          <p className="mt-2 text-sm text-slate-500" data-id="element-228" data-mp="3w34zjldjagcc">
            Vozila znamke {brand.name} prodajamo in servisiramo v {brand.locations.length}{' '}
            {brand.locations.length === 1 ? 'centru' : 'centrih'}.
          </p>

          <ul className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-229" data-mp="21sf15384mftg">
            {contacts.map(contact => <li key={contact.place} data-id="element-230" data-mp="1xsejzatn7qip">
                <article className="flex h-full flex-col border border-slate-200 p-6" data-id="element-231" data-mp="1xp8wu14kqypm">
                  <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-aw-navy" data-id="element-232" data-mp="1su3a503clg0h">
                    <MapPin className="h-4 w-4 text-aw-accent" aria-hidden="true" data-id="element-233" data-mp="2pgewyhkaowew" />
                    {contact.place}
                  </h3>
                  <a href={`mailto:${contact.mail}`} className="mt-4 flex items-center gap-2 text-sm text-slate-600 hover:text-aw-navy" data-id="element-234" data-mp="2gh1o9r3zwbv8">
                    <Mail className="h-4 w-4 text-slate-400" aria-hidden="true" data-id="element-235" data-mp="1537e1qruzynf" />
                    {contact.mail}
                  </a>
                  <a href={`tel:${contact.phone.replace(/\s/g, '')}`} className="mt-2 flex items-center gap-2 text-sm text-slate-600 hover:text-aw-navy" data-id="element-236" data-mp="10205hphw6vfp">
                    <Phone className="h-4 w-4 text-slate-400" aria-hidden="true" data-id="element-237" data-mp="uwmvgis7vokx" />
                    {contact.phone}
                  </a>
                </article>
              </li>)}
          </ul>
        </div>
      </section>
    </>;
}